import { produce } from "immer";
import { isEqual, memoize } from "lodash";
import { useEffect, useRef } from "react";
import { Permission, Plugin, Seller } from "types";
import create from "zustand";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";

interface GlobalStoreInterface {
    isInitialized: boolean;
    installedPlugins: Plugin[];
    arePluginsLoaded: boolean;
    permissions: {
        isWaitlistEnabled: boolean;
        allowPastDates: boolean;
        showAffiliates: boolean;
        canModifyTaxesAndFees: boolean;
        canSearchCustomers: boolean;
        showPackages: boolean;
        isInvoiceEnabled: boolean;
        canCreateBooking: boolean;
        canViewDashboard: boolean;
        canViewPurchases: boolean;
        canViewCustomers: boolean;
        canExportCustomers: boolean;
        canSearchPurchaseFromDashboard: boolean;
        canViewMessageTemplates: boolean;
        canViewWaivers: boolean;
        canViewProducts: boolean;
        canViewProductCatalog: boolean;
        canViewGuides: boolean;
        canViewEquipments: boolean;
        shouldShowReports: boolean;
        canViewReports: boolean;
        canViewMarketing: boolean;
        canViewDistribution: boolean;
        canViewAppStore: boolean;
        canViewSettings: boolean;
        canViewXsite: boolean;
        canViewAdminMenu: boolean;
        canViewReportAnalytics: boolean;
        canViewReportCustomers: boolean;
        canViewReportEarnings: boolean;
        canViewReportGiftOffers: boolean;
        canViewReportPayouts: boolean;
        canViewReportAffiliateCommissions: boolean;
        canViewGratuity: boolean;
        canViewReportXolaBot: boolean;
        canViewCustomReports: boolean;
        canViewAffiliates: boolean;
        canViewOperators: boolean;
        canViewDistributionProfile: boolean;
        canViewCompanyProfile: boolean;
        canViewGuideProfile: boolean;
        canViewAccessManager: boolean;
        canViewNotificationSettings: boolean;
        canViewButtonCode: boolean;
        canViewPreferencesSettings: boolean;
        canViewGratuitySettings: boolean;
        canViewAppsSettings: boolean;
        canViewTaxesAndFees: boolean;
        canViewPaymentsSettings: boolean;
        canViewHardware: boolean;
        canViewCompanyBasicInfo: boolean;
        canViewMultiLanguageSupport: boolean;
        canViewAbandoned: boolean;
        canViewConversionTracking: boolean;
        canViewCoupons: boolean;
        canViewXolaBot: boolean;
        canViewServerSideTracking: boolean;
        canViewCustomConversionTracking: boolean;
        canViewGiftOffers: boolean;
        canViewPackages: boolean;
        canViewMemberships: boolean;
        canViewMerchandise: boolean;
        canViewPartnerPackages: boolean;
        canViewGlobalSettings: boolean;
        canViewNotifications: boolean;
        canApplyCoupons: boolean;
        canViewPartnerExperiences: boolean;
        canViewPurchase: boolean;
        canViewGuestStatus: boolean;
        canViewAmountDue: boolean;
        canModifyWaivers: boolean;
        canViewDisputeReport: boolean;
        canViewCustomersSettings: boolean;
    };

    initialize: (isGranted: (permission: Permission) => boolean) => void;
}

const useGlobalStore = create<GlobalStoreInterface>((set) => ({
    isInitialized: false,
    installedPlugins: [],
    arePluginsLoaded: false,
    permissions: {
        isWaitlistEnabled: false,
        allowPastDates: false,
        showAffiliates: false,
        canModifyTaxesAndFees: false,
        canSearchCustomers: false,
        showPackages: false,
        isInvoiceEnabled: false,
        canCreateBooking: false,
        canViewDashboard: false,
        canViewPurchases: false,
        canViewCustomers: false,
        canExportCustomers: false,
        canViewMessageTemplates: false,
        canViewWaivers: false,
        canViewProducts: false,
        canViewProductCatalog: false,
        canSearchPurchaseFromDashboard: false,
        canViewGuides: false,
        canViewEquipments: false,
        shouldShowReports: false,
        canViewReports: false,
        canViewMarketing: false,
        canViewDistribution: false,
        canViewAppStore: false,
        canViewSettings: false,
        canViewXsite: false,
        canViewAdminMenu: false,
        canViewReportAnalytics: false,
        canViewReportCustomers: false,
        canViewReportEarnings: false,
        canViewReportGiftOffers: false,
        canViewReportPayouts: false,
        canViewReportAffiliateCommissions: false,
        canViewGratuity: false,
        canViewReportXolaBot: false,
        canViewCustomReports: false,
        canViewAffiliates: false,
        canViewOperators: false,
        canViewDistributionProfile: false,
        canViewCompanyProfile: false,
        canViewGuideProfile: false,
        canViewAccessManager: false,
        canViewNotificationSettings: false,
        canViewButtonCode: false,
        canViewPreferencesSettings: false,
        canViewGratuitySettings: false,
        canViewAppsSettings: false,
        canViewTaxesAndFees: false,
        canViewPaymentsSettings: false,
        canViewHardware: false,
        canViewCompanyBasicInfo: false,
        canViewMultiLanguageSupport: false,
        canViewAbandoned: false,
        canViewConversionTracking: false,
        canViewCoupons: false,
        canViewXolaBot: false,
        canViewServerSideTracking: false,
        canViewCustomConversionTracking: false,
        canViewGiftOffers: false,
        canViewPackages: false,
        canViewMemberships: false,
        canViewMerchandise: false,
        canViewPartnerPackages: false,
        canViewGlobalSettings: false,
        canViewNotifications: false,
        canApplyCoupons: false,
        canViewPartnerExperiences: false,
        canViewPurchase: false,
        canViewGuestStatus: false,
        canViewAmountDue: false,
        canModifyWaivers: false,
        canViewDisputeReport: false,
        canViewCustomersSettings: false,
    },

    areProductsLoaded: false,
    areExperiencesLoaded: false,

    initialize(isGranted) {
        set((state) => {
            return produce(state, (draft) => {
                draft.permissions.isWaitlistEnabled = isGranted(Permission.WAITLISTS_VIEW);
                draft.permissions.allowPastDates = isGranted(Permission.ALLOW_PAST_DATES);
                draft.permissions.showAffiliates = isGranted(Permission.AFFILIATES_VIEW);
                draft.permissions.canModifyTaxesAndFees = isGranted(Permission.MODIFY_TAXES_AND_FEES);
                draft.permissions.canSearchCustomers = isGranted(Permission.SEARCH_CUSTOMERS);
                draft.permissions.showPackages = isGranted(Permission.PACKAGES_VIEW);
                draft.permissions.isInvoiceEnabled = isGranted(Permission.INVOICE_VIEW);
                draft.permissions.canCreateBooking = isGranted(Permission.ADD_BOOKING_VIEW);
                draft.permissions.canViewDashboard = isGranted(Permission.DASHBOARD_VIEW);
                draft.permissions.canApplyCoupons = isGranted(Permission.APPLY_COUPONS);
                draft.permissions.canViewPurchases =
                    isGranted(Permission.PURCHASES_VIEW) && isGranted(Permission.MODIFY_PURCHASE_CONTACT_VIEW);
                draft.permissions.canSearchPurchaseFromDashboard = isGranted(Permission.DASHBOARD_SEARCH_VIEW);
                draft.permissions.canViewCustomers = isGranted(Permission.CUSTOMERS_VIEW);
                draft.permissions.canExportCustomers = isGranted(Permission.CUSTOMERS_EXPORT);
                draft.permissions.canViewMessageTemplates = isGranted(Permission.MESSAGE_TEMPLATES_VIEW);
                draft.permissions.canViewWaivers = isGranted(Permission.WAIVERS_PERMISSIONS_VIEW);
                draft.permissions.canViewProducts = isGranted(Permission.PRODUCTS_VIEW);
                draft.permissions.canViewProductCatalog = isGranted(Permission.PRODUCT_CATALOG_VIEW);
                draft.permissions.canViewEquipments = isGranted(Permission.MODIFY_RESOURCES_EQUIPMENT);
                draft.permissions.canViewGuides = isGranted(Permission.MODIFY_RESOURCES_GUIDE);
                draft.permissions.shouldShowReports =
                    isGranted(Permission.REPORTS_ANALYTICS_VIEW) ||
                    isGranted(Permission.REPORTS_CUSTOMERS_VIEW) ||
                    isGranted(Permission.REPORTS_EARNINGS_VIEW) ||
                    isGranted(Permission.REPORTS_GIFT_OFFERS_VIEW) ||
                    isGranted(Permission.REPORTS_PAYOUTS_VIEW) ||
                    isGranted(Permission.GRATUITY_VIEW) ||
                    isGranted(Permission.REPORTS_CUSTOM_REPORTS_VIEW) ||
                    isGranted(Permission.REPORTS_AFFILIATE_COMMISSIONS_VIEW);
                draft.permissions.canViewReports = isGranted(Permission.REPORTS_VIEW);
                draft.permissions.canViewMarketing = isGranted(Permission.MARKETING_VIEW);
                draft.permissions.canViewDistribution =
                    isGranted(Permission.AFFILIATES_VIEW) || isGranted(Permission.OPERATORS_VIEW);
                draft.permissions.canViewAppStore = isGranted(Permission.APP_STORE_VIEW);
                draft.permissions.canViewSettings = isGranted(Permission.SETTINGS_VIEW);
                draft.permissions.canViewXsite = isGranted(Permission.XSITE_VIEW);
                draft.permissions.canViewAdminMenu = isGranted(Permission.XOLA_ADMIN);
                draft.permissions.canViewReportAnalytics = isGranted(Permission.REPORTS_ANALYTICS_VIEW);
                draft.permissions.canViewReportCustomers = isGranted(Permission.REPORTS_CUSTOMERS_VIEW);
                draft.permissions.canViewReportEarnings = isGranted(Permission.REPORTS_EARNINGS_VIEW);
                draft.permissions.canViewReportGiftOffers = isGranted(Permission.REPORTS_GIFT_OFFERS_VIEW);
                draft.permissions.canViewReportPayouts = isGranted(Permission.REPORTS_PAYOUTS_VIEW);
                draft.permissions.canViewReportAffiliateCommissions = isGranted(
                    Permission.REPORTS_AFFILIATE_COMMISSIONS_VIEW,
                );
                draft.permissions.canViewGratuity = isGranted(Permission.GRATUITY_VIEW);
                draft.permissions.canViewReportXolaBot = isGranted(Permission.REPORTS_XOLABOT_VIEW); // Added property
                draft.permissions.canViewCustomReports = isGranted(Permission.REPORTS_CUSTOM_REPORTS_VIEW); // Added property
                draft.permissions.canViewAffiliates = isGranted(Permission.AFFILIATES_VIEW);
                draft.permissions.canViewOperators = isGranted(Permission.OPERATORS_VIEW);
                draft.permissions.canViewDistributionProfile = isGranted(Permission.DISTRIBUTION_PROFILE_VIEW);
                draft.permissions.canViewCompanyProfile = isGranted(Permission.COMPANY_PROFILE_VIEW);
                draft.permissions.canViewGuideProfile = isGranted(Permission.GUIDE_PROFILE_VIEW);
                draft.permissions.canViewAccessManager = isGranted(Permission.ACCESS_MANAGER_VIEW);
                draft.permissions.canViewNotificationSettings = isGranted(Permission.NOTIFICATIONS_SETTINGS_VIEW);
                draft.permissions.canViewButtonCode = isGranted(Permission.BUTTON_CODE_VIEW);
                draft.permissions.canViewPreferencesSettings = isGranted(Permission.PREFERENCES_SETTINGS_VIEW);
                draft.permissions.canViewGratuitySettings = isGranted(Permission.GRATUITY_SETTINGS_VIEW);
                draft.permissions.canViewAppsSettings = isGranted(Permission.APPS_SETTINGS_VIEW);
                draft.permissions.canViewTaxesAndFees = isGranted(Permission.TAXES_AND_FEES_VIEW);
                draft.permissions.canViewPaymentsSettings = isGranted(Permission.PAYMENTS_SETTINGS_VIEW);
                draft.permissions.canViewHardware = isGranted(Permission.HARDWARE_VIEW);
                draft.permissions.canViewCompanyBasicInfo = isGranted(Permission.COMPANY_BASIC_INFO_VIEW);
                draft.permissions.canViewMultiLanguageSupport = isGranted(Permission.MULTI_LANGUAGE_SUPPORT_VIEW);
                draft.permissions.canViewAbandoned = isGranted(Permission.ABANDONED_VIEW);
                draft.permissions.canViewConversionTracking = isGranted(Permission.CONVERSION_TRACKING_VIEW);
                draft.permissions.canViewCoupons = isGranted(Permission.COUPONS_VIEW);
                draft.permissions.canViewXolaBot = isGranted(Permission.XOLABOT_VIEW);
                draft.permissions.canViewServerSideTracking = isGranted(Permission.SERVER_SIDE_TRACKING_VIEW);
                draft.permissions.canViewCustomConversionTracking = isGranted(
                    Permission.CUSTOM_CONVERSION_TRACKING_VIEW,
                );
                draft.permissions.canViewNotifications = isGranted(Permission.NOTIFICATIONS_VIEW);
                draft.permissions.canViewGiftOffers = isGranted(Permission.GIFT_OFFERS_VIEW);
                draft.permissions.canViewPackages = isGranted(Permission.PACKAGES_VIEW);
                draft.permissions.canViewMemberships = isGranted(Permission.MEMBERSHIPS_VIEW);
                draft.permissions.canViewMerchandise = isGranted(Permission.MERCHANDISE_VIEW);
                draft.permissions.canViewPartnerPackages = isGranted(Permission.PARTNER_PACKAGES_VIEW);
                draft.permissions.canViewGlobalSettings = isGranted(Permission.PRODUCT_CATALOG_VIEW);
                draft.permissions.canViewPartnerExperiences = isGranted(Permission.PARTNER_EXPERIENCES_VIEW);

                draft.isInitialized = true;
                draft.permissions.canViewPurchase = isGranted(Permission.PURCHASES_VIEW);
                draft.permissions.canViewGuestStatus = isGranted(Permission.GUEST_STATUS);
                draft.permissions.canViewAmountDue = isGranted(Permission.AMOUNT_DUE_VIEW);
                draft.permissions.canModifyWaivers = isGranted(Permission.MODIFY_WAIVER_VIEW);

                draft.permissions.canViewDisputeReport = isGranted(Permission.DISPUTE_REPORT_VIEW);
                draft.permissions.canViewCustomersSettings = isGranted(Permission.CUSTOMERS_SETTINGS_VIEW);
            });
        });
    },
}));

export const useInitializeGlobalStore = () => {
    const { isGranted } = useAuthorization();

    const seller = useSeller();

    const lastUpdatedSeller = useRef<Seller | null>(null);

    useEffect(() => {
        if (
            isEqual(lastUpdatedSeller.current?.preferences, seller.preferences) &&
            isEqual(lastUpdatedSeller.current?.roles, seller.roles)
        ) {
            return;
        }

        // Reinitialize the store if the seller has changed
        useGlobalStore.getState().initialize(isGranted);
        lastUpdatedSeller.current = seller;
    }, [seller, isGranted]);

    if (!useGlobalStore.getState().isInitialized) {
        useGlobalStore.getState().initialize(isGranted);
        lastUpdatedSeller.current = seller;
    }
};

export const isWaitlistEnabled = () => {
    return useGlobalStore.getState().permissions.isWaitlistEnabled;
};

export const allowPastDates = () => {
    return useGlobalStore.getState().permissions.allowPastDates;
};

export const canShowAffiliates = () => {
    return useGlobalStore.getState().permissions.showAffiliates;
};

export const canModifyTaxesAndFees = () => {
    return useGlobalStore.getState().permissions.canModifyTaxesAndFees;
};

export const canSearchCustomers = () => {
    return useGlobalStore.getState().permissions.canSearchCustomers;
};

export const showPackages = () => {
    return useGlobalStore.getState().permissions.showPackages;
};

export const canViewPartnerPackages = () => {
    return useGlobalStore.getState().permissions.canViewPartnerPackages;
};

export const isInvoiceEnabled = () => {
    return useGlobalStore.getState().permissions.isInvoiceEnabled;
};

export const canApplyCoupons = () => {
    return useGlobalStore.getState().permissions.canApplyCoupons;
};

export const getPermissions = memoize(() => {
    return useGlobalStore.getState().permissions;
});

export const canViewPurchase = () => {
    return useGlobalStore.getState().permissions.canViewPurchase;
};

export const canViewGuestStatus = () => {
    return useGlobalStore.getState().permissions.canViewGuestStatus;
};

export const canViewAmountDue = () => {
    return useGlobalStore.getState().permissions.canViewAmountDue;
};

export const canViewWaivers = () => {
    return hasWaiverPlugin() && useGlobalStore.getState().permissions.canViewWaivers;
};

export const canModifyWaivers = () => {
    return hasWaiverPlugin() && useGlobalStore.getState().permissions.canModifyWaivers;
};

export const canViewCustomersSettings = () => {
    return useGlobalStore.getState().permissions.canViewCustomersSettings;
};

export const useCanExportCustomers = () => useGlobalStore((state) => state.permissions.canExportCustomers);

export const useCanViewXsite = () => useGlobalStore((state) => state.permissions.canViewXsite);

// Derived from the shared installedPlugins snapshot rather than a separate flag.
export const hasWaiverPlugin = () => {
    return useGlobalStore.getState().installedPlugins.some((plugin) => plugin.abilities?.waiver);
};

// Populated once from useAuthorization so the many embedded-app trigger locations read a
// shared snapshot instead of each re-subscribing to the plugins query and refetching on mount.
// Guarded by isEqual because the source query hands back a new array reference on every poll.
// `arePluginsLoaded` separates "no apps installed" from "query still in flight", which consumers
// need before they can treat a missing plugin as unavailable.
export const setInstalledPluginsInStore = (plugins: Plugin[], arePluginsLoaded: boolean) => {
    const state = useGlobalStore.getState();
    if (!isEqual(state.installedPlugins, plugins)) {
        useGlobalStore.setState({ installedPlugins: plugins });
    }

    if (state.arePluginsLoaded !== arePluginsLoaded) {
        useGlobalStore.setState({ arePluginsLoaded });
    }
};

// Reactive: the plugin list loads async, so consumers must re-render once it lands.
export const useInstalledPluginsFromStore = () => useGlobalStore((state) => state.installedPlugins);

export const useArePluginsLoadedFromStore = () => useGlobalStore((state) => state.arePluginsLoaded);

// Non-reactive read of the shared snapshot, for use outside React (e.g. the embedded-app RPC host).
export const getInstalledPlugins = () => useGlobalStore.getState().installedPlugins;
